import SectionMessage from '@atlaskit/section-message';
import {
  LinkClickedEvent,
  LozengeClickedEvent,
  MarketplaceAnalyticsContext,
  MenuExpandedEvent,
  MenuItemClickedEvent,
  TabClickedEvent,
  useAnalyticsEvents,
} from '@atlassian/mpac-analytics';
import {
  AppListing,
  AppListingTab,
  ELEMENT_ID,
  MediaViewerResource,
} from '@atlassian/mpac-app-listing';
import { useFeatureFlags } from '@atlassian/mpac-feature-flags';
import Telemetry from '@atlassian/mpac-telemetry';
import { AppDeployment, HostingTypeToQueryParam } from '@atlassian/mpac-types';
import {
  AtlassianProductHostingType,
  MarketplaceAppPaymentModel,
} from '@atlassian/mpac-types/dist/GraphQLGateway.gql.types';
import { ExternalLink, PartnerPermissions } from '@atlassian/mpac-ui';
import { DOMUtils, MarketplaceAppUtils, UrlUtils } from '@atlassian/mpac-utils';
import capitalize from 'lodash/capitalize';
import React, { useLayoutEffect } from 'react';

import {
  safeToForwardToPartner,
  trackDownloadLink,
  trackLightboxView,
  trackLookingForDataCenter,
  trackSeeMoreReviews,
  trackVendorSupportLinkClick,
  trackWatchAction,
} from '../../../../../../../../common/utils/analytics/analytics';
import {
  useAppListingPageAnalytics,
  useGoogleAnalyticsExtraContext,
  useVendorGoogleAnalytics,
} from '../../../../../../../../hooks';
import {
  ANALYTICS_CATEGORY_APP_LISTING,
  ANALYTICS_EVENT_CONTAINER_APP_HEADER,
  ANALYTICS_EVENT_CONTAINER_NAV_BAR,
  ANALYTICS_EVENT_CONTAINER_OVERVIEW_TAB,
  ANALYTICS_SUBJECT_ID_APP_LISTING_TAB,
  ANALYTICS_SUBJECT_ID_CLOUD_MIGRATION_INFORMATION,
  ANALYTICS_SUBJECT_ID_CONTACT_VENDOR,
  ANALYTICS_SUBJECT_ID_HOSTING_SELECT,
  ANALYTICS_SUBJECT_ID_MIGRATION_ASSISTANCE,
} from '../../../../../../../analytics/Events';
import BootstrapConfig from '../../../../../../../bootstrapConfig';
import useCloudMigrationInfo from '../../../../../../../hooks/useCloudMigration';
import useLoggedInUser from '../../../../../../../hooks/useLoggedInUser';
import AdminHeaderBanners from '../AdminHeaderBanners/AdminHeaderBanners';
import AdminHeaderLinks from '../AdminHeaderLinks';
import AppListingJSDWidget from '../AppListingJSDWidget';
import AppListingMeta from '../AppListingMeta/AppListingMeta';
import {
  getEmceeAppListingInstallLink,
  getEmceeAppListingRequestAppLink,
  onlyJiraConfluenceProducts,
} from '../AppsRoute.utils';
import HeaderInjection from '../HeaderInjection';
import SiteSelectorDialogWrapper from '../HeaderInjection/SiteSelectorDialogWrapper';
import { SITE_SELECTOR_VISIBLE_QUERY_PARAM, useCTAClickHandler } from '../hooks/useCTAClickHandler';
import usePartnerPermissions from '../hooks/usePartnerPermissions';
import {
  getMigrationAssistanceStatus,
  getMigrationInformationAvailable,
  hasNewerPrivateVersion,
  toAnalyticsHosting,
} from '../utils';
import { EOLWarningContainer } from './styles';

import type {
  AppListingTabsType,
  AppVersion,
  HostingOptionKindType,
  MarketplaceAppByKeyQuery,
  MediaViewerResourceType,
  SupportResourcesLinkType,
} from '@atlassian/mpac-app-listing';
import type {
  AppListingCloudMigration,
  AppListingUser,
  BootstrapLinks,
} from '@atlassian/mpac-types';

type BeforeHeaderContentProps = {
  appId: string;
  links: BootstrapLinks;
  appDetails: NonNullable<MarketplaceAppByKeyQuery['marketplaceAppByKey']>;
  showOnlyPublic: boolean;
};

const BeforeHeaderContent = ({
  links,
  appId,
  appDetails,
  showOnlyPublic,
}: BeforeHeaderContentProps) => {
  const hasNewerPvtVersionForAnyHosting =
    hasNewerPrivateVersion(appDetails.privateCloudVersion, appDetails.publicCloudVersion) ||
    hasNewerPrivateVersion(appDetails.privateServerVersion, appDetails.publicServerVersion) ||
    hasNewerPrivateVersion(appDetails.privateDataCenterVersion, appDetails.publicDataCenterVersion);

  return (
    <>
      <AdminHeaderBanners
        appManageLink={links.appManageLink.expand({ appId })}
        hasNewerPrivateVersion={hasNewerPvtVersionForAnyHosting}
        listingStatus={appDetails.listingStatus}
        showOnlyPublic={showOnlyPublic}
      />
      <AdminHeaderLinks
        appManageLink={links.appManageLink.expand({ appId })}
        appShortLink={links.appShortLink.expand({ appId })}
      />
    </>
  );
};

type AppListingPageContentProps = {
  appDetails: NonNullable<MarketplaceAppByKeyQuery['marketplaceAppByKey']>;
  appId: string;
  setNewTab: (tab: AppListingTabsType) => void;
  setNewHostingType: (hostingType: AtlassianProductHostingType) => void;
  selectedHosting: AtlassianProductHostingType;
  selectedAppVersion: AppVersion;
  tab: AppListingTabsType;
  links: BootstrapLinks;
  showOnlyPublic: boolean;
};
// eslint-disable-next-line max-lines-per-function
const AppListingPageContent = (
  props: AppListingPageContentProps & {
    cloudMigrationInfo: AppListingCloudMigration | null | undefined;
  }
) => {
  const { isLoggedIn, isAdmin, userAvatarUrl, user } = useLoggedInUser();
  const {
    appDetails,
    appId,
    selectedHosting,
    selectedAppVersion,
    tab,
    showOnlyPublic,
    links,
    cloudMigrationInfo,
  } = props;
  const {
    mpacBaseUrl,
    jiraServiceDeskEmbedBaseUrl,
    mpacCdnBaseUrl,
    atlassianCommunityBaseUrl,
    feedbackCollectorApiKeys,
    feedbackGatewayBaseUrl,
  } = BootstrapConfig.get();

  const fireAnalyticsEvent = useAnalyticsEvents();
  const {
    enableWebpImage,
    enableGoogleAnalytics4Tracking,
    enableUserSessionAuthForMpacToEmceeRedirect,
    enableMpacServerEolWarning,
  } = useFeatureFlags();
  const { permissions } = usePartnerPermissions(mpacBaseUrl, appDetails.partner?.id, isLoggedIn);

  const serverAppVersion = appDetails.serverAppVersions.edges?.[0]?.node;
  const dataCenterAppVersion = appDetails.dataCenterAppVersions.edges?.[0]?.node;
  const cloudAppVersion = appDetails.cloudAppVersions.edges?.[0]?.node;
  const versionInfo = {
    cloudAppVersion,
    serverAppVersion,
    dataCenterAppVersion,
  };

  useAppListingPageAnalytics({
    appName: appDetails.name,
    appId,
  });

  useVendorGoogleAnalytics({
    appId,
    appKey: appDetails.appKey,
    googleAnalyticsId: appDetails.googleAnalyticsId,
    buildNumber: selectedAppVersion.buildNumber,
    appName: appDetails.name,
  });

  // Track using Google analytics 4 additionally if feature flag is on and ga4 id is present.
  // Once ga4 migration is complete, the flag and ga3 tracking will be removed.
  // https://ecosystem.atlassian.net/browse/AMKT-29181
  useVendorGoogleAnalytics({
    appId,
    appKey: appDetails.appKey,
    googleAnalyticsId: enableGoogleAnalytics4Tracking ? appDetails.googleAnalytics4Id : null,
    buildNumber: selectedAppVersion.buildNumber,
    appName: appDetails.name,
  });

  useGoogleAnalyticsExtraContext({
    cloudAppVersion,
    serverAppVersion,
    dataCenterAppVersion,
  });

  useLayoutEffect(() => {
    Telemetry.timeToFirstMeaningfulPaint(performance.now());
  }, []);

  const instrumentHostingClick = (
    hosting: AtlassianProductHostingType,
    hostingOptionKind: HostingOptionKindType
  ) => {
    fireAnalyticsEvent({
      eventType: MenuItemClickedEvent,
      source: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
      actionSubjectId: ANALYTICS_SUBJECT_ID_HOSTING_SELECT,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
        label: toAnalyticsHosting(hosting),
        hosting: toAnalyticsHosting(hosting),
        selectionType: hostingOptionKind,
      },
    });
  };

  const handleTabChange = (newTab: AppListingTabsType) => {
    props.setNewTab(newTab);
    fireAnalyticsEvent({
      eventType: TabClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_APP_LISTING_TAB,
      source: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
      attributes: {
        label: capitalize(newTab),
        eventContainer: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
        appListingTabKey: capitalize(newTab),
      },
    });
  };

  const handleHostingChange = (hosting: AtlassianProductHostingType) => {
    props.setNewHostingType(hosting);
  };

  const handleVendorContactClick = (hostingType: string) => {
    fireAnalyticsEvent({
      eventType: LinkClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_CONTACT_VENDOR,
      source: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
        source: hostingType,
      },
    });
  };

  const handleCloudMigrationResourcesLinksClick = (label: string) => {
    fireAnalyticsEvent({
      eventType: LinkClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_CLOUD_MIGRATION_INFORMATION,
      source: ANALYTICS_EVENT_CONTAINER_OVERVIEW_TAB,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_OVERVIEW_TAB,
        label,
      },
    });
  };

  const handleSupportLinkClick = (supportLink: SupportResourcesLinkType) => {
    safeToForwardToPartner(appId, () => {
      trackVendorSupportLinkClick(supportLink);
    });
  };

  const handleMediaViewerOpen = (resourceType: MediaViewerResourceType, imageUrl: string) => {
    safeToForwardToPartner(appId, () => {
      trackLightboxView(imageUrl, resourceType === MediaViewerResource.Highlight, mpacCdnBaseUrl);
    });
  };

  const handleWatchAppToggle = (toggleOn: boolean) => {
    safeToForwardToPartner(appId, () => {
      trackWatchAction(toggleOn ? 'Watch' : 'Unwatch');
    });
  };

  const handleSeeMoreReviewsClick = () => {
    safeToForwardToPartner(appId, () => {
      trackSeeMoreReviews();
    });
  };

  const handleArtifactDownloadClick = (url: string) => {
    const { pathname } = new URL(url);
    safeToForwardToPartner(appId, () => {
      trackDownloadLink(pathname);
    });
  };

  const handleHostingSelectOpen = () => {
    fireAnalyticsEvent({
      eventType: MenuExpandedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_HOSTING_SELECT,
      source: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_NAV_BAR,
        hostingOptions: appDetails.productHostingOptions.map(toAnalyticsHosting),
      },
    });
  };

  const handleHostingOptionClick = (
    hosting: AtlassianProductHostingType,
    hostingOptionKind: HostingOptionKindType
  ) => {
    instrumentHostingClick(hosting, hostingOptionKind);

    if (hosting === AtlassianProductHostingType.DataCenter) {
      safeToForwardToPartner(appId, () => {
        trackLookingForDataCenter();
      });
    }
  };

  const handleMigrationLozengeClick = () => {
    handleTabChange(AppListingTab.Overview);
    setTimeout(() => {
      DOMUtils.scrollToAndFocusElement(`#${ELEMENT_ID.CLOUD_MIGRATION_SECTION_LINKS}`);
    }, 0);
    fireAnalyticsEvent({
      eventType: LozengeClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_MIGRATION_ASSISTANCE,
      attributes: {
        eventContainer: ANALYTICS_EVENT_CONTAINER_APP_HEADER,
      },
    });
  };

  const {
    siteSelectorOpen,
    onSiteSelectorClose,
    hostingSelectorOpen,
    onHostingSelectorClose,
    activeCTAKind,
    hostingSelectorPurchaseIntent,
    onRequestAppClick,
    onAppListingHeaderCTAClick,
    onHostingSelectorCTAClick,
    onPricingCalculatorCTAClick,
  } = useCTAClickHandler({
    appKey: appDetails.appKey,
    appId,
    userLoggedIn: isLoggedIn,
    userEmail: user?.email,
    selectedHosting,
    paymentModel: selectedAppVersion.paymentModel,
    compatibleProducts: selectedAppVersion.deployment.compatibleProducts,
    cloudAppVersion,
    artifactDownloadLink: links.artifactDownload,
    enableUserSessionAuthForMpacToEmceeRedirect: !!enableUserSessionAuthForMpacToEmceeRedirect,
  });

  const getHeaderInjection = () => {
    const hasPublicCloudVersion = !!appDetails.publicCloudVersion.edges?.length;
    const hasPrivateCloudVersion = !!appDetails.privateCloudVersion.edges?.length;

    // An app is private remoteable only, if it has no public version
    // and at-least one private version
    const isPrivateRemoteableOnly = !hasPublicCloudVersion && hasPrivateCloudVersion;
    return (
      <HeaderInjection
        appKey={appDetails.appKey}
        appId={appId}
        appName={appDetails.name}
        selectedHosting={selectedHosting}
        privateListingsLink={links.managePrivateListing}
        privateRemotableOnly={isPrivateRemoteableOnly}
        appDetailsLink={links.appDetails}
        artifactDownloadLink={links.artifactDownload}
        trialLicenseActivationLink={links.trialLicenseActivation}
        licensePurchaseLink={links.licensePurchase}
        versionInfo={versionInfo}
        productHostingOptions={appDetails.productHostingOptions}
        hostingSelectorOpen={hostingSelectorOpen}
        onHostingSelectorClose={onHostingSelectorClose}
        activeCTAKind={activeCTAKind}
        hostingSelectorPurchaseIntent={hostingSelectorPurchaseIntent}
        onRequestAppClick={onRequestAppClick}
        onAppListingHeaderCTAClick={onAppListingHeaderCTAClick}
        onHostingSelectorCTAClick={onHostingSelectorCTAClick}
      />
    );
  };

  const renderBeforeHeader = () => {
    return (
      <BeforeHeaderContent
        appId={appId}
        appDetails={appDetails}
        links={links}
        showOnlyPublic={showOnlyPublic}
      />
    );
  };

  const contentAfterHeader = (
    <EOLWarningContainer data-testid="server-eol-warning-message">
      <SectionMessage appearance="warning">
        <p>
          As we continue to move towards a cloud future together, new server app sales and installs
          are no longer available for customers. You can update app version via Atlassian
          Marketplace until the end of support for server on Feb 15, 2024.{' '}
          <ExternalLink href="https://developer.atlassian.com/platform/marketplace/knowledge-base/end-of-sale-and-support-for-server-apps/">
            Learn more
          </ExternalLink>
        </p>
      </SectionMessage>
    </EOLWarningContainer>
  );

  const metaTagImage =
    selectedAppVersion.heroImage ||
    (selectedAppVersion.highlights?.length && selectedAppVersion.highlights[0].croppedScreenshot) ||
    appDetails.logo;

  const jiraConfluenceCloudAppCompatibilities = cloudAppVersion
    ? onlyJiraConfluenceProducts(selectedAppVersion.deployment.compatibleProducts).map(
        (product) => product.id
      )
    : [];

  const installAppHrefResolver = (selectedSiteProduct) =>
    getEmceeAppListingInstallLink(
      appDetails.appKey,
      selectedSiteProduct,
      user?.email,
      !!enableUserSessionAuthForMpacToEmceeRedirect
    );

  const requestAppHrefResolver = (selectedSiteProduct) =>
    getEmceeAppListingRequestAppLink(
      appDetails.appKey,
      selectedSiteProduct,
      user?.email,
      !!enableUserSessionAuthForMpacToEmceeRedirect
    );

  const loginRedirectPath = UrlUtils.updateQueryParam(
    window.location.href,
    SITE_SELECTOR_VISIBLE_QUERY_PARAM,
    'true'
  );
  const appListingUser: AppListingUser = {
    isMpacAdmin: isAdmin,
    isLoggedIn,
    avatarUrl: userAvatarUrl,
    userId: user?.id,
  };

  const { showInstallationTab, showServerEolWarning } = enableMpacServerEolWarning
    ? MarketplaceAppUtils.isServerAppEol(
        selectedHosting === AtlassianProductHostingType.Server,
        selectedAppVersion?.deployment?.compatibleProducts
      )
    : { showInstallationTab: true, showServerEolWarning: false };

  return (
    <>
      <AppListingMeta
        appId={appDetails.appId}
        appName={appDetails.name}
        appSlug={appDetails.slug}
        appDescription={appDetails.tagline}
        canonicalLink={links.appListingCanonical}
        mediaImageLink={enableWebpImage ? links.mediaImage : links.mediaFile}
        image={metaTagImage}
        logo={appDetails.logo}
      />

      <SiteSelectorDialogWrapper
        appId={appDetails.appId}
        installAppHrefResolver={installAppHrefResolver}
        requestAppHrefResolver={requestAppHrefResolver}
        appCompatibilities={jiraConfluenceCloudAppCompatibilities}
        appKey={appDetails.appKey}
        appName={appDetails.name}
        paymentModel={selectedAppVersion.paymentModel}
        appDetailsLink={links.appDetails}
        onClose={onSiteSelectorClose}
        isOpen={siteSelectorOpen}
        loginRedirectPath={loginRedirectPath}
        mpacBaseUrl={mpacBaseUrl}
      />

      <PartnerPermissions.Provider value={permissions}>
        <AppListing
          appDetails={appDetails}
          components={{
            beforeHeader: renderBeforeHeader(),
            ...(showServerEolWarning && { afterHeader: contentAfterHeader }),
          }}
          user={appListingUser}
          loginHref={`/login?redirect=${encodeURIComponent(window.location.href)}`}
          mpacBaseUrl={mpacBaseUrl}
          feedbackCollectorApiKeys={feedbackCollectorApiKeys}
          feedbackGatewayBaseUrl={feedbackGatewayBaseUrl}
          mpacCdnBaseUrl={mpacCdnBaseUrl}
          atlassianCommunityBaseUrl={atlassianCommunityBaseUrl}
          headerInjection={getHeaderInjection()}
          links={links}
          selectedHosting={selectedHosting}
          selectedTab={tab}
          cloudMigration={cloudMigrationInfo}
          onHostingChange={handleHostingChange}
          onTabSelect={handleTabChange}
          onSupportLinkClick={handleSupportLinkClick}
          onMediaViewerOpen={handleMediaViewerOpen}
          onWatchAppToggle={handleWatchAppToggle}
          onSeeMoreReviewsClick={handleSeeMoreReviewsClick}
          onArtifactDownloadClick={handleArtifactDownloadClick}
          onHostingSelectOpen={handleHostingSelectOpen}
          onHostingOptionClick={handleHostingOptionClick}
          onContactPartnerClick={handleVendorContactClick}
          onCloudMigrationResourceLinksClick={handleCloudMigrationResourcesLinksClick}
          onMigrationLozengeClick={handleMigrationLozengeClick}
          openMarketplaceLinksInNewTab={false}
          showHostingSelect
          showResourcesSection
          showInstallationTab={showInstallationTab}
          showVersionsTab
          showReviewsTab
          showInstallationCount
          showIntegrationsDetailsSection
          showPricingTab
          showHeaderProductCompatibilities
          showHeaderLozenges
          showCloudMigrationLozenge={true}
          onPricingCalculatorInstallCTAClick={onPricingCalculatorCTAClick}
        />
      </PartnerPermissions.Provider>

      {props.appDetails.jsdWidgetKey && (
        <AppListingJSDWidget
          jiraServiceDeskEmbedBaseUrl={jiraServiceDeskEmbedBaseUrl}
          jsdWidgetKey={props.appDetails.jsdWidgetKey}
        />
      )}
    </>
  );
};

const AppListingWithAnalyticsContext = (props: AppListingPageContentProps) => {
  const { appDetails, appId, selectedHosting, tab, selectedAppVersion } = props;
  const hosting = HostingTypeToQueryParam[selectedHosting];
  const { appKey } = appDetails || {};
  const paymentModel =
    selectedAppVersion.paymentModel === MarketplaceAppPaymentModel.Free ? 'free' : 'paid';
  const isEcosystemCloudApp =
    selectedAppVersion.deployment.__typename === AppDeployment.ECOSYSTEM_CLOUD;
  const { mpacBaseUrl } = BootstrapConfig.get();
  const cloudMigrationInfo = useCloudMigrationInfo(mpacBaseUrl, appKey);

  const analyticsContext = {
    appId,
    partnerId: appDetails?.partner?.id,
    appKey,
    appListingTabKey: tab,
    category: ANALYTICS_CATEGORY_APP_LISTING,
    hosting,
    isEcosystemCloudApp,
    paymentModel,
    alternateCloudAppKey: cloudMigrationInfo.alternateCloudAppKey,
    migrationAssistanceStatus: getMigrationAssistanceStatus(cloudMigrationInfo),
    migrationInformationAvailable: getMigrationInformationAvailable(cloudMigrationInfo),
  };

  return (
    <MarketplaceAnalyticsContext attributes={analyticsContext}>
      <AppListingPageContent {...props} cloudMigrationInfo={cloudMigrationInfo} />
    </MarketplaceAnalyticsContext>
  );
};

export default AppListingWithAnalyticsContext;
